import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, CheckCircle, Camera, Storefront, User, Wrench, Bank } from '@phosphor-icons/react';
import API from '../lib/api';
import { useAuth } from '../lib/auth';

const MACHINES = [
  { id: 'digital_paper', label: 'Digital Paper (Konica / Xerox)' },
  { id: 'flex_large_format', label: 'Flex / Large Format' },
  { id: 'vinyl_cutter', label: 'Vinyl Cutter' },
  { id: 'sublimation', label: 'Sublimation' },
  { id: 'canopy_assembly', label: 'Canopy Assembly' },
];

const STEPS = [
  { key: 'store', label: 'Store', icon: Storefront },
  { key: 'contact', label: 'Contact', icon: User },
  { key: 'machine', label: 'Machine', icon: Wrench },
  { key: 'verify', label: 'Verify', icon: Camera },
  { key: 'bank', label: 'Banking', icon: Bank },
];

const PartnerSignup = () => {
  const nav = useNavigate();
  const { user, register, refresh } = useAuth();
  const [step, setStep] = useState(0);
  const [cats, setCats] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  // Account (created on step 2 submit)
  const [acc, setAcc] = useState({ email: '', password: '', name: '', phone: '' });
  const [d, setD] = useState({
    shop_name: '', address: '', gmap_link: '',
    service_pincodes: '',
    machine_types: [], machine_brand: '',
    machine_photo_file_id: null, machine_photo_name: null,
    bank_account: '', bank_ifsc: '', upi_id: '', gst: '',
    allowed_categories: [],
    accept_tnc: false,
  });

  useEffect(() => { API.get('/categories').then(r => setCats(r.data)).catch(() => {}); }, []);

  const toggleArr = (key, val) => setD(f => ({
    ...f,
    [key]: f[key].includes(val) ? f[key].filter(x => x !== val) : [...f[key], val],
  }));

  const canProceed = () => {
    if (step === 0) return d.shop_name && d.address;
    if (step === 1) return acc.name && acc.email && acc.phone && (user || acc.password);
    if (step === 2) return d.machine_types.length > 0;
    if (step === 3) return !!d.machine_photo_file_id;
    if (step === 4) return d.upi_id && d.accept_tnc;
    return false;
  };

  const uploadPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error('Max 5MB'); return; }
    // Need to be logged in to upload — create account first if not
    if (!user) {
      // Register first with minimum info collected so far
      try {
        const u = await register({ email: acc.email, password: acc.password, name: acc.name, phone: acc.phone, role: 'partner' });
        toast.success('Account created — uploading photo…');
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Please complete contact step first');
        return;
      }
    }
    const fd = new FormData();
    fd.append('file', f);
    try {
      const r = await API.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setD({ ...d, machine_photo_file_id: r.data.file_id, machine_photo_name: r.data.name });
      toast.success('Photo uploaded');
    } catch (err) { toast.error('Upload failed'); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      // Make sure account exists
      if (!user) {
        await register({ email: acc.email, password: acc.password, name: acc.name, phone: acc.phone, role: 'partner' });
      }
      const pincodes = d.service_pincodes.split(',').map(s => s.trim()).filter(Boolean);
      await API.post('/partners/onboarding', {
        shop_name: d.shop_name,
        address: `${d.address}${d.gmap_link ? ' | Map: ' + d.gmap_link : ''}`,
        service_pincodes: pincodes,
        machine_types: d.machine_types,
        machine_brand: d.machine_brand,
        machine_photo_file_id: d.machine_photo_file_id,
        bank_account: d.bank_account,
        bank_ifsc: d.bank_ifsc,
        upi_id: d.upi_id,
        gst: d.gst,
        allowed_categories: d.allowed_categories,
        accept_tnc: d.accept_tnc,
      });
      await refresh();
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed');
    } finally { setBusy(false); }
  };

  if (submitted) {
    return (
      <div className="p-6 text-center">
        <CheckCircle size={48} weight="fill" color="var(--pk-primary)" className="mx-auto" />
        <h1 className="font-display text-[28px] mt-3">Application received</h1>
        <p className="text-[13px] text-neutral-600 mt-2">
          Our team reviews new print partners within 24 hours. You'll get access to the partner dashboard once approved.
        </p>
        <div className="mt-6 pk-card p-4 text-left text-[12px]">
          <div className="font-bold">What happens next</div>
          <ol className="mt-2 space-y-1 list-decimal list-inside text-neutral-700">
            <li>Team verifies your details, machine photo & banking</li>
            <li>You receive an approval email</li>
            <li>Log in — jobs start arriving in your dashboard</li>
          </ol>
        </div>
        <button onClick={() => nav('/')} className="pk-btn w-full mt-6">Back to home</button>
      </div>
    );
  }

  return (
    <div className="p-6">
      <Link to="/register" className="text-[10px] tracking-[0.24em] uppercase font-bold inline-flex items-center gap-1 text-neutral-500"><ArrowLeft size={12}/> Back</Link>
      <div className="mt-3 reg-mark">
        <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: 'var(--pk-primary)' }}>— Partner application</div>
        <h1 className="font-display text-[28px] mt-2 leading-tight">Join the network.</h1>
        <p className="text-[12px] text-neutral-600 mt-1">5 steps · reviewed by our team · usually approved in 24 hrs.</p>
      </div>

      {/* Step indicator */}
      <div className="mt-5 flex items-center justify-between">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          const active = i === step;
          const done = i < step;
          return (
            <div key={s.key} className="flex-1 flex flex-col items-center gap-1">
              <div className={`w-9 h-9 border-2 border-black flex items-center justify-center ${active ? 'text-white' : done ? 'text-white' : 'bg-white'}`}
                style={{ background: active ? 'var(--pk-primary)' : done ? '#10b981' : '#fff' }}>
                <Icon size={16} weight="fill" />
              </div>
              <div className="text-[9px] tracking-[0.18em] uppercase font-bold">{s.label}</div>
              {i < STEPS.length - 1 && <div className="hidden" />}
            </div>
          );
        })}
      </div>

      <div className="mt-6 space-y-3">
        {step === 0 && <>
          <div className="text-[10px] tracking-[0.22em] uppercase text-neutral-500 font-bold">Step 1 · Store details</div>
          <div><label className="pk-label">Store name</label><input required className="pk-input" value={d.shop_name} onChange={e => setD({ ...d, shop_name: e.target.value })} data-testid="ps-shop"/></div>
          <div><label className="pk-label">Full address</label><textarea rows={3} className="pk-textarea" value={d.address} onChange={e => setD({ ...d, address: e.target.value })} data-testid="ps-addr"/></div>
          <div><label className="pk-label">Google Maps link (paste your shop's pin URL)</label><input className="pk-input font-mono text-[11px]" placeholder="https://maps.app.goo.gl/…" value={d.gmap_link} onChange={e => setD({ ...d, gmap_link: e.target.value })} data-testid="ps-gmap"/></div>
          <div><label className="pk-label">Service pincodes (comma separated)</label><input className="pk-input font-mono" placeholder="560001, 560068" value={d.service_pincodes} onChange={e => setD({ ...d, service_pincodes: e.target.value })}/></div>
        </>}

        {step === 1 && <>
          <div className="text-[10px] tracking-[0.22em] uppercase text-neutral-500 font-bold">Step 2 · Contact & account</div>
          <div><label className="pk-label">Your name</label><input required className="pk-input" value={acc.name} onChange={e => setAcc({ ...acc, name: e.target.value })}/></div>
          <div><label className="pk-label">Email</label><input required type="email" className="pk-input" value={acc.email} onChange={e => setAcc({ ...acc, email: e.target.value })}/></div>
          <div><label className="pk-label">Phone (10 digits)</label><input required inputMode="numeric" className="pk-input font-mono" value={acc.phone} onChange={e => setAcc({ ...acc, phone: e.target.value.replace(/\D/g, '') })}/></div>
          {!user && <div><label className="pk-label">Create a password</label><input required type="password" className="pk-input" value={acc.password} onChange={e => setAcc({ ...acc, password: e.target.value })}/></div>}
        </>}

        {step === 2 && <>
          <div className="text-[10px] tracking-[0.22em] uppercase text-neutral-500 font-bold">Step 3 · Machinery</div>
          <div><label className="pk-label">Machine types you own</label>
            <div className="flex flex-wrap gap-2">
              {MACHINES.map(m => <button type="button" key={m.id} onClick={() => toggleArr('machine_types', m.id)} className={`pk-chip ${d.machine_types.includes(m.id) ? 'active' : ''}`} data-testid={`ps-m-${m.id}`}>{m.label}</button>)}
            </div>
          </div>
          <div><label className="pk-label">Primary machine brand & model</label><input className="pk-input" placeholder="e.g. Konica bizhub C750i" value={d.machine_brand} onChange={e => setD({ ...d, machine_brand: e.target.value })}/></div>
          <div><label className="pk-label">Categories you can fulfill</label>
            <div className="flex flex-wrap gap-2">
              {cats.map(c => <button type="button" key={c.category_id} onClick={() => toggleArr('allowed_categories', c.category_id)} className={`pk-chip ${d.allowed_categories.includes(c.category_id) ? 'active' : ''}`}>{c.name}</button>)}
            </div>
          </div>
        </>}

        {step === 3 && <>
          <div className="text-[10px] tracking-[0.22em] uppercase text-neutral-500 font-bold">Step 4 · Verification photo</div>
          <p className="text-[12px] text-neutral-600">Click a live photo of your machine (with your shop background visible). This helps us verify authenticity — no stock images please.</p>
          <label className="mt-3 flex flex-col items-center justify-center py-8 pk-border border-dashed cursor-pointer hover:bg-yellow-50">
            <input type="file" accept="image/*" capture="environment" onChange={uploadPhoto} className="hidden" data-testid="ps-photo"/>
            <Camera size={32} weight="fill" color="var(--pk-primary)"/>
            <div className="font-bold text-[13px] mt-2">{d.machine_photo_name || 'Tap to take photo'}</div>
            <div className="text-[9px] tracking-[0.2em] uppercase text-neutral-500 mt-1">Live capture · JPG · ≤5 MB</div>
          </label>
          {d.machine_photo_file_id && <div className="text-[11px] text-green-700 flex items-center gap-1"><CheckCircle size={12} weight="fill"/> Photo captured</div>}
        </>}

        {step === 4 && <>
          <div className="text-[10px] tracking-[0.22em] uppercase text-neutral-500 font-bold">Step 5 · Banking</div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="pk-label">Bank account #</label><input className="pk-input font-mono" value={d.bank_account} onChange={e => setD({ ...d, bank_account: e.target.value })}/></div>
            <div><label className="pk-label">IFSC</label><input className="pk-input font-mono" value={d.bank_ifsc} onChange={e => setD({ ...d, bank_ifsc: e.target.value })}/></div>
          </div>
          <div><label className="pk-label">UPI ID (required for cashout)</label><input required className="pk-input font-mono" placeholder="yourshop@upi" value={d.upi_id} onChange={e => setD({ ...d, upi_id: e.target.value })}/></div>
          <div><label className="pk-label">GST (optional)</label><input className="pk-input font-mono" value={d.gst} onChange={e => setD({ ...d, gst: e.target.value })}/></div>
          <label className="flex items-start gap-2 pk-card p-3 mt-3">
            <input type="checkbox" checked={d.accept_tnc} onChange={e => setD({ ...d, accept_tnc: e.target.checked })} data-testid="ps-agree"/>
            <div className="text-[12px]">I accept PrintingKit's partner agreement — zero commission, direct settlement, quality warranty on my prints.</div>
          </label>
        </>}
      </div>

      <div className="mt-6 flex gap-2">
        {step > 0 && <button onClick={() => setStep(step - 1)} className="pk-btn pk-btn-outline flex-1" data-testid="ps-back">Back</button>}
        {step < STEPS.length - 1 ? (
          <button
            onClick={async () => {
              if (!canProceed()) { toast.error('Please complete all fields'); return; }
              // If moving from step 1 (contact) to step 2 without account, create one now
              if (step === 1 && !user) {
                try {
                  await register({ email: acc.email, password: acc.password, name: acc.name, phone: acc.phone, role: 'partner' });
                  toast.success('Account created — continuing…');
                } catch (err) { toast.error(err.response?.data?.detail || 'Registration failed'); return; }
              }
              setStep(step + 1);
            }}
            className="pk-btn flex-1"
            data-testid="ps-next"
          >Next <ArrowRight size={14} weight="bold"/></button>
        ) : (
          <button onClick={submit} disabled={!canProceed() || busy} className="pk-btn pk-btn-magenta flex-1" data-testid="ps-submit">
            {busy ? 'Submitting…' : 'Submit for review'}
          </button>
        )}
      </div>
    </div>
  );
};

export default PartnerSignup;
